import { useMemo } from 'react'
import { format, subDays, differenceInDays } from 'date-fns'
import { LineChartGraph } from '@/common/components/LineChart'
import { GetWithDrawDepositGraphResponse } from '@/modules/dashboard/type'

interface DepositWithdrawGraphProps {
	data?: GetWithDrawDepositGraphResponse
	isLoading?: boolean
	title?: string
	description?: string
	startDate?: string
	endDate?: string
	hideTitle?: boolean
	enableSliding?: boolean
}

export default function DepositWithdrawGraph({
	data,
	isLoading = false,
	title,
	description,
	startDate,
	endDate,
	hideTitle = false,
	enableSliding = false,
}: DepositWithdrawGraphProps) {
	// Calculate the date range and determine optimal settings
	const dateRangeInfo = useMemo(() => {
		let totalDays = 0

		// Calculate total days from data or date props
		if (data?.data && data.data.length > 0) {
			totalDays = data.data.length
		} else if (startDate && endDate) {
			totalDays = differenceInDays(new Date(endDate), new Date(startDate)) + 1
		}

		// Simple logic: enable sliding only if more than 30 days
		const shouldEnableSliding = totalDays > 40

		// Basic range options when sliding is enabled
		const rangeOptions = shouldEnableSliding
			? [
					{ label: '7D', days: 7 },
					{ label: '1M', days: 30 },
					{ label: '3M', days: 90 },
					{ label: '6M', days: 180 },
					{ label: '1Y', days: 365 },
					{ label: 'All', days: null },
				]
			: []

		return {
			totalDays,
			shouldEnableSliding: enableSliding && shouldEnableSliding,
			dateFormat: 'dd MMM', // Consistent format
			rangeOptions,
		}
	}, [data, startDate, endDate, enableSliding])

	const graphData = useMemo(() => {
		if (!data?.data || !Array.isArray(data.data) || data.data.length === 0)
			return []

		// Use the optimized date format
		return data.data.map((item) => ({
			week: format(new Date(item.date), dateRangeInfo.dateFormat),
			deposit: item.deposit,
			withdraw: item.withdraw,
		}))
	}, [data, dateRangeInfo.dateFormat])

	// Format the date range for display
	const dateRangeText = useMemo(() => {
		// Prefer the actual data range when API returns multiple days
		const items = data?.data
		if (items && items.length > 1) {
			const firstDate = new Date(items[0].date)
			const lastDate = new Date(items[items.length - 1].date)
			return `${format(firstDate, 'dd MMM yyyy')} - ${format(lastDate, 'dd MMM yyyy')}`
		}

		// Fall back to provided start/end if both present
		if (startDate && endDate) {
			const startFormatted = format(new Date(startDate), 'dd MMM yyyy')
			const endFormatted = format(new Date(endDate), 'dd MMM yyyy')
			return `${startFormatted} - ${endFormatted}`
		}

		// If only one date is provided, show the last 7 days ending on that date
		const single = endDate || startDate
		if (single) {
			const end = new Date(single)
			const start = subDays(end, 6)
			return `${format(start, 'dd MMM yyyy')} - ${format(end, 'dd MMM yyyy')}`
		}

		return ''
	}, [data, startDate, endDate])

	const chartTitle = `ยอดฝาก/ถอน${dateRangeText ? ` (${dateRangeText})` : ''}`
	const chartDescription = 'แสดงข้อมูลยอดฝาก ถอนตามช่วงเวลาที่เลือก'

	if (isLoading) {
		return (
			<div className="rounded-lg border bg-card p-6">
				<div className="space-y-4">
					{!hideTitle && (
						<>
							{/* Title skeleton */}
							<div className="space-y-2">
								<div className="h-6 w-64 animate-pulse rounded bg-muted"></div>
								<div className="h-4 w-96 animate-pulse rounded bg-muted"></div>
							</div>
						</>
					)}

					{/* Chart skeleton */}
					<div className="flex h-64 animate-pulse items-center justify-center rounded bg-muted">
						<div className="text-muted-foreground">กำลังโหลดข้อมูล...</div>
					</div>
				</div>
			</div>
		)
	}

	// Check if data is empty
	if (!data?.data || !Array.isArray(data.data) || data.data.length === 0) {
		return (
			<div className="rounded-lg border bg-card p-6">
				<div className="space-y-4">
					{!hideTitle && (
						<>
							{/* Title */}
							<div className="space-y-2">
								<h3 className="text-lg font-semibold text-foreground">
									{title || chartTitle}
								</h3>
								<p className="text-sm text-muted-foreground">
									{description || chartDescription}
								</p>
							</div>
						</>
					)}

					{/* No data message */}
					<div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-border">
						<div className="text-center">
							<div className="mb-2 text-muted-foreground">
								<svg
									className="mx-auto h-12 w-12"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1}
										d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
									/>
								</svg>
							</div>
							<p className="font-medium text-muted-foreground">ไม่มีข้อมูล</p>
							<p className="mt-1 text-sm text-muted-foreground">
								ไม่พบข้อมูลยอดฝาก/ถอนในช่วงเวลาที่เลือก
							</p>
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<LineChartGraph
			data={graphData}
			title={hideTitle ? undefined : title || chartTitle}
			description={hideTitle ? undefined : description || chartDescription}
			enableSliding={dateRangeInfo.shouldEnableSliding}
			rangeOptions={dateRangeInfo.rangeOptions}
			dataKeys={{
				deposit: { label: 'ยอดฝาก', color: 'hsl(var(--chart-2))' },
				withdraw: { label: 'ยอดถอน', color: 'hsl(var(--chart-3))' },
			}}
		/>
	)
}
